(() => {
  if (customElements.get('predictive-search') || typeof SearchForm !== 'function') return;

  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.cachedResults = Object.create(null);
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.isOpen = false;
      this.searchTerm = '';
      this.requestSequence = 0;
      this.abortController = null;
      this.setupEventListeners();
    }

    get isHeaderSearch() {
      return Boolean(this.closest('[data-miniami-header]'));
    }

    setupEventListeners() {
      if (this.listenersBound) return;
      this.listenersBound = true;
      this.onImmediateInputHandler = () => {
        if (this.dismissedQuery !== this.getQuery()) this.dismissedQuery = null;
        if (this.searchTerm === this.getQuery()) return;
        this.cancelPendingRequest();
        this.removeAttribute('results');
        this.closeResults();
        this.predictiveSearchResults?.replaceChildren();
        this.clearError();
      };
      this.onSubmitHandler = this.onFormSubmit.bind(this);
      this.onFocusHandler = this.onFocus.bind(this);
      this.onFocusOutHandler = this.onFocusOut.bind(this);
      this.onKeyupHandler = this.onKeyup.bind(this);
      this.onKeydownHandler = this.onKeydown.bind(this);
      this.input?.form?.addEventListener('submit', this.onSubmitHandler);
      this.input?.addEventListener('focus', this.onFocusHandler);
      this.input?.addEventListener('input', this.onImmediateInputHandler);
      this.addEventListener('focusout', this.onFocusOutHandler);
      this.addEventListener('keyup', this.onKeyupHandler);
      this.addEventListener('keydown', this.onKeydownHandler);
    }

    disconnectedCallback() {
      this.cancelPendingRequest();
      this.input?.form?.removeEventListener('submit', this.onSubmitHandler);
      this.input?.removeEventListener('focus', this.onFocusHandler);
      this.input?.removeEventListener('input', this.onImmediateInputHandler);
      this.removeEventListener('focusout', this.onFocusOutHandler);
      this.removeEventListener('keyup', this.onKeyupHandler);
      this.removeEventListener('keydown', this.onKeydownHandler);
      this.listenersBound = false;
    }

    connectedCallback() {
      this.setupEventListeners();
    }

    clearError() {
      this.removeAttribute('data-search-error');
      this.querySelector('[data-predictive-error]')?.remove();
    }

    getQuery() {
      return this.input?.value.trim() || '';
    }

    onChange() {
      super.onChange();
      const previousTerm = this.searchTerm;
      const newSearchTerm = this.getQuery();
      if (this.dismissedQuery === newSearchTerm || !this.isConnected) return;
      this.updateSearchForTerm(previousTerm, newSearchTerm);
      this.searchTerm = newSearchTerm;
      this.cancelPendingRequest();
      this.removeAttribute('results');
      // The template field shares the same query ownership as header search.
      this.clearError();
      this.closeResults();
      if (this.predictiveSearchResults) this.predictiveSearchResults.replaceChildren();

      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) event.preventDefault();
      const selected = this.querySelector('[aria-selected="true"] a');
      // Input debounce may not have run yet. Never send a new query to an old
      // keyboard-selected product or consume a selection from a closed sheet.
      if (selected && this.hasAttribute('results') && this.hasAttribute('open') && this.searchTerm === this.getQuery()) {
        event.preventDefault();
        selected.click();
      }
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (!super.shouldResetForm()) return;
      this.searchTerm = '';
      this.cancelPendingRequest();
      this.closeResults(true);
    }

    onFocus() {
      this.dismissedQuery = null;
      const currentSearchTerm = this.getQuery();
      if (!currentSearchTerm.length) return;
      if (this.searchTerm !== currentSearchTerm) {
        this.onChange();
      } else if (this.hasAttribute('results')) {
        this.open();
      } else {
        this.getSearchResults(currentSearchTerm);
      }
    }

    onFocusOut() {
      window.setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      }, 0);
    }

    onKeyup(event) {
      if (!this.getQuery().length) this.close(true);
      if (event.code === 'ArrowUp') this.switchOption('up');
      if (event.code === 'ArrowDown') this.switchOption('down');
    }

    onKeydown(event) {
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
      if (event.code === 'Escape') {
        event.preventDefault();
        this.close();
        this.input?.focus({ preventScroll: true });
      }
    }

    updateSearchForTerm(previousTerm, newTerm) {
      if (!previousTerm || previousTerm === newTerm) return;
      const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
      if (!searchForTextElement) return;
      searchForTextElement.textContent = searchForTextElement.textContent.replace(previousTerm, newTerm);
    }

    switchOption(direction) {
      if (!this.hasAttribute('open')) return;
      const options = [...this.querySelectorAll('[data-predictive-search-product][role="option"]')];
      if (!options.length) return;
      const selectedIndex = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
      const delta = direction === 'up' ? -1 : 1;
      const nextIndex = selectedIndex < 0
        ? (direction === 'up' ? options.length - 1 : 0)
        : (selectedIndex + delta + options.length) % options.length;
      options.forEach((option, index) => option.setAttribute('aria-selected', String(index === nextIndex)));
      this.input?.setAttribute('aria-activedescendant', options[nextIndex].id);
    }

    selectOption() {
      this.querySelector('[aria-selected="true"] a')?.click();
    }

    cancelPendingRequest() {
      clearTimeout(this.requestDeadline);
      this.requestSequence += 1;
      this.setAttribute('data-request-sequence', String(this.requestSequence));
      if (this.abortController) this.abortController.abort();
      this.abortController = null;
      this.removeAttribute('loading');
      if (this.isHeaderSearch) this.setAttribute('aria-busy', 'false');
    }

    isAbortError(error) {
      return error?.name === 'AbortError' || error?.code === 20;
    }

    getSearchResults(searchTerm) {
      this.clearError();
      const queryKey = searchTerm.toLowerCase();
      const sequence = ++this.requestSequence;
      this.setAttribute('data-request-sequence', String(sequence));
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey], sequence);
        return;
      }

      const controller = this.abortController;
      this.requestDeadline = setTimeout(() => {
        if (sequence !== this.requestSequence) return;
        this.requestTimedOut = sequence;
        controller.abort();
      }, 15000);
      fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
        signal: this.abortController.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`predictive search ${response.status}`);
          return response.text();
        })
        .then((text) => {
          if (sequence !== this.requestSequence || searchTerm !== this.searchTerm) return;
          const parsed = new DOMParser().parseFromString(text, 'text/html');
          const section = parsed.querySelector('#shopify-section-predictive-search') || parsed;
          const fragment = section.querySelector('[data-predictive-search-fragment]');
          if (!fragment) throw new Error('Predictive search fragment missing');
          const resultsMarkup = fragment.innerHTML;
          this.cachedResults[queryKey] = resultsMarkup;
          this.renderSearchResults(resultsMarkup, sequence);
        })
        .catch((error) => {
          if (sequence !== this.requestSequence || (this.isAbortError(error) && this.requestTimedOut !== sequence)) return;
          this.removeAttribute('loading');
          this.close();
          this.setAttribute('data-search-error', '');
          let message = this.querySelector('[data-predictive-error]');
          if (!message) {
            message = document.createElement('p');
            message.setAttribute('data-predictive-error', '');
            message.className = 'miniami-predictive-search__empty';
            message.setAttribute('role', 'status');
            this.append(message);
          }
          message.textContent = 'Search suggestions are unavailable. Press Enter to search all results.';
          if (this.statusElement) {
            this.statusElement.setAttribute('aria-hidden', 'false');
            this.statusElement.textContent = 'Search suggestions are unavailable. Try again, or press Enter to search.';
          }
        }).finally(() => {
          if (sequence === this.requestSequence) clearTimeout(this.requestDeadline);
        });
    }

    renderSearchResults(resultsMarkup, sequence) {
      if (sequence !== this.requestSequence || !this.predictiveSearchResults) return;
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.predictiveSearchResults
        .querySelectorAll('[data-predictive-search-product]')
        .forEach((product, index) => {
          if (index >= 3) product.remove();
        });
      // Predictive resources are capped at10 and use a different search engine;
      // their array size is not a complete storefront search-result total.
      const productsShown = this.predictiveSearchResults.querySelectorAll('[data-predictive-search-product]').length;
      const categoriesShown = this.predictiveSearchResults.querySelectorAll('[data-predictive-search-category]').length;
      const heading = this.predictiveSearchResults.querySelector('.miniami-predictive-search__heading');
      if (heading) heading.textContent = `Suggestions · ${productsShown} shown`;
      const count = this.predictiveSearchResults.querySelector('[data-predictive-search-live-region-count-value]');
      if (count) {
        const shown = productsShown + categoriesShown;
        count.dataset.totalResults = String(shown);
        count.textContent = shown ? `${shown} search ${shown === 1 ? 'suggestion' : 'suggestions'}. Press Enter to search all results.` : 'No suggestions. Press Enter to search all results.';
      }
      this.scopeResultIds();
      this.setAttribute('results', 'true');
      this.setLiveRegionResults();
      this.open();
    }

    scopeResultIds() {
      const idPrefix = `${this.input?.id || 'predictive-search'}-`;
      const idMap = new Map();
      this.predictiveSearchResults.querySelectorAll('[id]').forEach((element) => {
        const originalId = element.id;
        const scopedId = `${idPrefix}${originalId}`;
        idMap.set(originalId, scopedId);
        element.id = scopedId;
      });
      this.predictiveSearchResults
        .querySelectorAll('[aria-labelledby], [aria-describedby], [aria-controls], [aria-owns], [aria-activedescendant]')
        .forEach((element) => {
          ['aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns', 'aria-activedescendant'].forEach((attribute) => {
            const value = element.getAttribute(attribute);
            if (!value) return;
            element.setAttribute(attribute, value.split(/\s+/).map((reference) => idMap.get(reference) || reference).join(' '));
          });
        });
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      if (this.statusElement) {
        this.statusElement.setAttribute('aria-hidden', 'false');
        this.statusElement.textContent = this.getAttribute('data-loading-text') || 'Loading';
      }
      this.setAttribute('loading', 'true');
      if (this.isHeaderSearch) this.setAttribute('aria-busy', 'true');
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');
      if (this.isHeaderSearch) this.setAttribute('aria-busy', 'false');
      const count = this.querySelector('[data-predictive-search-live-region-count-value]');
      if (this.statusElement) {
        this.statusElement.setAttribute('aria-hidden', 'false');
        this.statusElement.textContent = count?.textContent?.trim() || '';
      }
    }

    getTotalResultCount() {
      return Number(this.predictiveSearchResults?.querySelector('[data-total-results]')?.dataset.totalResults || 0);
    }

    open() {
      if (!this.predictiveSearchResults) return;
      this.predictiveSearchResults.style.maxHeight = `${Math.max(0, window.innerHeight - (document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0))}px`;
      this.setAttribute('open', 'true');
      this.input?.setAttribute('aria-expanded', 'true');
      this.isOpen = true;
    }

    close(clearSearchTerm = false) {
      this.dismissedQuery = this.getQuery();
      // Escape/focus-out must cancel the template request too, not just header.
      this.cancelPendingRequest();
      if (this.isHeaderSearch) {
        this.removeAttribute('data-search-error');
        if (this.statusElement) this.statusElement.textContent = '';
      }
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        if (this.input) this.input.value = '';
        this.removeAttribute('results');
      }
      this.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', 'false');
      this.input?.setAttribute('aria-activedescendant', '');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.input?.setAttribute('aria-expanded', 'false');
      if (this.predictiveSearchResults) this.predictiveSearchResults.removeAttribute('style');
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();